import { useEffect, useState } from "react";
import { BadgeCheck, KeyRound, Mail, ShieldCheck } from "lucide-react";
import Waveform from "../components/Waveform";
import { Reveal, MaskLine } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function Access() {
  const [email, setEmail] = useState("");
  const [wlState, setWlState] = useState("idle");
  const [wlMsg, setWlMsg] = useState("");
  const [wlPosition, setWlPosition] = useState(null);
  const [count, setCount] = useState(null);

  const [code, setCode] = useState("");
  const [codeState, setCodeState] = useState("idle");
  const [codeMsg, setCodeMsg] = useState("");
  const [codeLabel, setCodeLabel] = useState("");

  useEffect(() => {
    fetch(`${API}/waitlist/count`)
      .then((r) => r.json())
      .then((d) => setCount(d.count))
      .catch(() => {});
  }, []);

  const submitWaitlist = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setWlState("error");
      setWlMsg("Enter a valid email address.");
      return;
    }
    setWlState("loading");
    setWlMsg("");
    try {
      const res = await fetch(`${API}/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong. Try again.");
      setWlPosition(data.position);
      setCount(data.total);
      setWlState("done");
      setWlMsg(data.duplicate ? "You're already on the list — still counts." : "");
    } catch (err) {
      setWlState("error");
      setWlMsg(err.message);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    const value = code.trim();
    if (!value) {
      setCodeState("error");
      setCodeMsg("Enter your access code.");
      return;
    }
    setCodeState("loading");
    setCodeMsg("");
    try {
      const res = await fetch(`${API}/access-code/validate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: value }),
      });
      const data = await res.json();
      if (!res.ok || !data.valid) throw new Error(data.detail || "That code isn't valid.");
      setCodeState("granted");
      setCodeLabel(data.label || "Early access");
    } catch (err) {
      setCodeState("error");
      setCodeMsg(err.message);
    }
  };

  return (
    <section className="relative overflow-hidden pt-32 sm:pt-40 pb-20" data-testid="page-access">
      <div className="glow-blob" style={{ width: 560, height: 560, top: "-20%", right: "-10%" }} aria-hidden="true" />
      <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
        <p className="eyebrow" data-testid="access-eyebrow">
          <span className="live-dot" /> Early access
        </p>
        <h1 className="hero-title mt-6 !text-[clamp(2rem,4.6vw,3.4rem)]" data-testid="access-title">
          <MaskLine delay={0.05}>Get in</MaskLine>
          <MaskLine delay={0.18}>
            <span className="text-gradient">first.</span>
          </MaskLine>
        </h1>
        <p className="body-lg max-w-xl mt-6">
          Two ways into the room before launch — join the waitlist, or redeem an access code if
          you have one.
        </p>

        <div className="grid lg:grid-cols-2 gap-6 mt-14">
          <Reveal testid="waitlist-card">
            <div className="glass rounded-3xl p-7 sm:p-9 h-full">
              <span className="icon-chip"><Mail size={20} /></span>
              <h2 className="font-display text-xl mt-5">Be the first to know when we go live!</h2>
              <p className="body-lg mt-3 text-[0.95rem]">
                Join the waitlist and we'll email you the moment rooms open.
              </p>
              {wlState === "done" ? (
                <div className="success-panel mt-7 p-5" data-testid="waitlist-success">
                  <p className="font-display text-lg flex items-center gap-2.5">
                    <BadgeCheck size={20} className="text-mint" /> You're on the list — #{wlPosition}
                  </p>
                  <p className="text-sm text-dusk mt-2">
                    {wlMsg || "We'll be in touch the moment your spot opens."}
                  </p>
                </div>
              ) : (
                <form onSubmit={submitWaitlist} className="mt-7" noValidate>
                  <label htmlFor="waitlist-email" className="sr-only">Email address</label>
                  <input
                    id="waitlist-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="waitlist-email-input"
                    className={`field ${wlState === "error" ? "state-error" : ""}`}
                  />
                  {wlState === "error" && (
                    <p className="error-text mt-2" data-testid="waitlist-error">{wlMsg}</p>
                  )}
                  <button
                    type="submit"
                    data-testid="waitlist-submit-button"
                    disabled={wlState === "loading"}
                    className="btn-primary mt-4 w-full justify-center disabled:opacity-60 disabled:pointer-events-none"
                  >
                    {wlState === "loading" ? "Joining…" : "Join the waitlist"}
                  </button>
                </form>
              )}
              <p className="mt-5 text-xs font-mono text-dusk" data-testid="waitlist-count">
                {count === null ? "Checking the line…" : `${count} ${count === 1 ? "person is" : "people are"} already in line.`}
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1} testid="code-card">
            <div className="glass rounded-3xl p-7 sm:p-9 h-full">
              <span className="icon-chip"><KeyRound size={20} /></span>
              <h2 className="font-display text-xl mt-5">Have an access code?</h2>
              <p className="body-lg mt-3 text-[0.95rem]">
                Codes come from our team, partners and friends already inside. They're
                case-insensitive.
              </p>
              {codeState === "granted" ? (
                <div className="granted-pass mt-7" data-testid="access-code-success">
                  <p className="font-display text-lg flex items-center gap-2.5 text-mint">
                    <ShieldCheck size={20} /> Access granted
                  </p>
                  <p className="text-sm text-dusk mt-2">
                    {codeLabel} — you're on the list for the first wave of invites.
                  </p>
                  <Waveform bars={28} className="h-8 mt-5" testid="access-granted-waveform" />
                </div>
              ) : (
                <form onSubmit={submitCode} className="mt-7" noValidate>
                  <label htmlFor="access-code" className="sr-only">Access code</label>
                  <input
                    id="access-code"
                    type="text"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck="false"
                    placeholder="e.g. 2026LAUNCH"
                    value={code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    data-testid="access-code-input"
                    className={`field font-mono tracking-[0.2em] uppercase ${codeState === "error" ? "state-error" : ""}`}
                  />
                  {codeState === "error" && (
                    <p className="error-text mt-2" data-testid="access-code-error">{codeMsg}</p>
                  )}
                  <button
                    type="submit"
                    data-testid="access-code-submit-button"
                    disabled={codeState === "loading"}
                    className="btn-ghost mt-4 w-full justify-center disabled:opacity-60 disabled:pointer-events-none"
                  >
                    {codeState === "loading" ? "Checking…" : "Unlock access"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
